// The native apps on the website (D344–D346, docs/installed-app.md §7): the
// store links come from the server's public config (APP_STORE_URL,
// PLAY_STORE_URL, ANDROID_APK_URL), and each badge shows only while its
// link is set, so a store is switched on without a release.

import type { Language } from '../i18n';
import { isAppleMobile } from './pwa';

export interface StoreLinks {
  appStore: string | null;
  playStore: string | null;
  apk: string | null;
}

export const NO_STORE_LINKS: StoreLinks = {
  appStore: null,
  playStore: null,
  apk: null,
};

/** The store links in the server's public config (GET /api/site). */
export function storeLinksOf(
  config:
    | {
        appStoreUrl?: string | null;
        playStoreUrl?: string | null;
        androidApkUrl?: string | null;
      }
    | null
    | undefined
): StoreLinks {
  if (!config) return NO_STORE_LINKS;
  return {
    appStore: config.appStoreUrl ?? null,
    playStore: config.playStoreUrl ?? null,
    apk: config.androidApkUrl ?? null,
  };
}

/** Which store this device can install from (D345). */
export type StoreDevice = 'ios' | 'android' | 'computer';

export function storeDevice(
  userAgent = navigator.userAgent,
  maxTouchPoints = navigator.maxTouchPoints ?? 0
): StoreDevice {
  if (isAppleMobile(userAgent, maxTouchPoints)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'computer';
}

export interface StoreChoice {
  appStore: string | null;
  playStore: string | null;
  apk: string | null;
  /** "Twinden works best as an app on your phone." (computers only) */
  worksBest: boolean;
}

/**
 * The badges for this device (D345): an iPhone or iPad gets the App Store
 * only, Android gets Google Play and the APK, a computer gets both stores
 * (no APK: it's for the phone) and the "works best on your phone" line.
 * A link that isn't set never shows.
 */
export function storeChoice(
  links: StoreLinks,
  device: StoreDevice
): StoreChoice {
  if (device === 'ios') {
    return {
      appStore: links.appStore,
      playStore: null,
      apk: null,
      worksBest: false,
    };
  }
  if (device === 'android') {
    return {
      appStore: null,
      playStore: links.playStore,
      apk: links.apk,
      worksBest: false,
    };
  }
  return {
    appStore: links.appStore,
    playStore: links.playStore,
    apk: null,
    worksBest: !!(links.appStore || links.playStore),
  };
}

export function hasAnyBadge(choice: StoreChoice): boolean {
  return !!(choice.appStore || choice.playStore || choice.apk);
}

/**
 * The store for this phone, which the install nudge and guide prefer to the
 * web app (D346); null on a computer or while the phone's store isn't set.
 * The APK is not a store: it needs "install unknown apps", so it stays in the
 * guide rather than one tap from the chats.
 */
export function phoneStoreUrl(
  links: StoreLinks,
  device: StoreDevice
): string | null {
  if (device === 'ios') return links.appStore;
  if (device === 'android') return links.playStore;
  return null;
}

/** The official artwork, served from Twinden (client/public/badges). */
export interface BadgeArt {
  src: string;
  /** The image's own size. */
  width: number;
  height: number;
  /** The badge inside it, without the transparent padding: x, y, w, h. */
  box: [number, number, number, number];
}

const APP_STORE_EN: BadgeArt = {
  src: '/badges/app-store-en.svg',
  width: 119.66407,
  height: 40,
  box: [0, 0, 119.66407, 40],
};

export const BADGES: {
  appStore: Record<Language, BadgeArt>;
  playStore: Record<Language, BadgeArt>;
} = {
  // Apple's Hindi badge comes only from its marketing tool; until it's added
  // the English one shows (badges/README.md). Never a homemade translation.
  appStore: { en: APP_STORE_EN, hi: APP_STORE_EN },
  playStore: {
    en: {
      src: '/badges/google-play-en.png',
      width: 646,
      height: 250,
      box: [41, 41, 564, 168],
    },
    hi: {
      src: '/badges/google-play-hi.png',
      width: 646,
      height: 250,
      box: [0, 29, 646, 192],
    },
  },
};

/**
 * How to draw a badge `height` px tall: the clipping frame's width, and the
 * image's size and offset inside it, so the visible badge (not its padding)
 * is that tall and every badge in a row matches.
 */
export function badgeLayout(art: BadgeArt, height: number) {
  const [x, y, w, h] = art.box;
  const scale = height / h;
  // Two decimals, and never -0.
  const px = (value: number) => Math.round(value * scale * 100) / 100 || 0;
  return {
    frameWidth: px(w),
    imageWidth: px(art.width),
    imageHeight: px(art.height),
    offsetX: px(-x),
    offsetY: px(-y),
  };
}
